"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Sparkles, ShieldCheck, PhoneCall, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const commitments = [{
  id: "cleanliness",
  title: "Professional Service",
  description: "We coordinate each rental around the customer's requested dates, delivery location, intended use, temperature requirements, placement details, site access, and equipment needs.",
  icon: Sparkles,
  tag: "Careful Coordination",
  focus: "Careful coordination from quote through pickup"
}, {
  id: "reliability",
  title: "Responsive Communication",
  description: "We focus on clear communication throughout the quote, scheduling, delivery, and pickup process.",
  icon: ShieldCheck,
  tag: "Responsive Communication",
  focus: "Clear updates through quote, scheduling, delivery, and pickup."
}, {
  id: "responsiveness",
  title: "Quality Equipment",
  description: "Fridge Fleet offers 6×8, 7×12, and 7×16 trailers with verified specifications, plus a 53-foot reefer trailer with typical dimensions and additional requirements confirmed individually.",
  icon: PhoneCall,
  tag: "Quality Equipment",
  focus: "Verified smaller-trailer specifications; 53-foot reefer details confirmed individually."
}];
export default function Commitment() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="our-commitment" data-nav="light" className="bg-background text-foreground py-16 md:py-24 lg:py-28 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 bg-accent/20 text-accent-foreground border-border px-3 py-1 text-xs font-semibold tracking-wide uppercase">
            <span className="inline-block size-1.5 rounded-full bg-chart-2 mr-1.5" />
            Core Values
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground text-balance">
            Our core commitments
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Family-owned and operated mobile refrigerated and freezer trailer rentals serving Southern California and the listed Las Vegas-area communities. Trailer availability and rental requirements are confirmed individually.
          </p>
        </div>

        {/* Commitment Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {commitments.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} variants={cardVariants} data-index={index}>
                <Card className="h-full border border-border bg-card text-card-foreground rounded-lg shadow-sm hover:border-primary/50 transition-[border-color,box-shadow] duration-200 flex flex-col justify-between p-6 sm:p-8">
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div className="size-12 rounded-lg bg-muted flex items-center justify-center text-foreground">
                        <IconComponent className="size-6" />
                      </div>
                      <span className="text-xs font-medium text-muted-foreground bg-muted/60 px-2.5 py-1 rounded-sm border border-border">
                        {item.tag}
                      </span>
                    </div>

                    <CardHeader className="p-0 mb-3">
                      <CardTitle className="text-xl font-bold font-default text-card-foreground leading-snug">
                        {item.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="p-0">
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </div>

                  <div className="mt-8 pt-4 border-t border-border flex items-center text-xs font-medium text-muted-foreground">
                    <CheckCircle2 className="size-4 text-chart-2 mr-2 shrink-0" />
                    <span>{item.focus}</span>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Action strip */}
        <div className="mt-12 md:mt-16 bg-card border border-border rounded-lg p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-xs">
          <div className="max-w-xl">
            <h3 className="text-lg sm:text-xl font-semibold font-default text-card-foreground">
              Need Refrigerated or Freezer Trailer Options?
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Request a quote to confirm trailer availability, delivery requirements, and rental details.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <Button asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestAQuotePage">
                Request a Quote
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <a href="tel:2135218821" className="inline-flex items-center text-sm font-medium text-muted-foreground py-2 px-3 border border-border rounded-md bg-muted/40">
              213-521-8821
            </a>
          </div>
        </div>
      </div>
    </section>;
}